import { useState } from 'react';
import { Info } from 'lucide-react';

type FieldHintProps = {
  label: string;
  text: string;
};

/** Compact inline info toggle for form field labels. */
export function FieldHint({ label, text }: FieldHintProps) {
  const [open, setOpen] = useState(false);

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>{label}</span>
      <button
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 hover:text-primary-600 dark:text-neutral-500 dark:hover:text-primary-400"
      >
        <Info size={14} />
      </button>
      {open ? (
        <span className="basis-full text-xs font-normal text-gray-500 dark:text-neutral-400 leading-snug">
          {text}
        </span>
      ) : null}
    </span>
  );
}

export const TRAFFIC_FLOW_DIRECTIONS_INTRO =
  'Forward follows your draw direction; backward runs against it. For one-way roads, set all lanes to the same direction.';

export const ROAD_FLOW_HINTS = {
  carriagewayCount:
    'How many separate paved strips this road has. One strip covers normal streets and undivided arterials. Two means a physical median — trace the centre divide once (default) or each carriageway separately.',
  totalLanes:
    'Enter the total marked lanes in this strip. Set each lane’s direction under Traffic Flow.',
  forwardLanes: 'Lane count on carriageway A (the first traced strip).',
  backwardLanes: 'Lane count on carriageway B (the opposite strip).',
  roadWidth:
    'Enter the motor carriageway width, from inner kerb to outer kerb. Excludes sidewalks, cycleways, shoulders and parking.',
  carriagewayAWidth:
    'Motor paved width (m) for carriageway A, kerb to kerb on that strip. Used to derive centreline and lane layout; excludes kerbside facilities.',
  carriagewayBWidth:
    'Motor paved width (m) for carriageway B, kerb to kerb on that strip. Can differ from A; excludes kerbside facilities.',
  stripCaptureMode:
    'How you capture geometry for this paved strip: centre line, outer kerb (width entered), or derived from the divide (divided roads only).',
  divideWidthM:
    'Physical width of the barrier between carriageways (metres). Leave 0 or empty when carriageways meet at a pavement seam with no gap.',
  dividedCarriageway:
    'Divided: trace the divide once, then configure each strip (centre, outer kerb, or derive from divide). Submit combines both strips.',
  dividedLaneDirections:
    'Set each lane direction per carriageway. L1, L2, … are ordered left to right facing your draw direction.',
  laneDirections:
    'Set each lane’s direction from left to right, facing your draw direction.',
  perLaneWidths:
    'Optional: enter each lane width in metres (L1 left → right). The sum must match the carriageway width (±0.1 m). Used for lane-axis arrows only — routing stays on the strip centreline.',
  kerbsideModelAIntro:
    'Facilities adjoining the carriageway. Detached footways and paths should be mapped separately (Model B).',
  carriagewayAccessIntro: 'Access permitted on the paved carriageway.',
  kerbsideSectionFooter:
    'Kerbside facilities are created as separate child features when the contribution is approved. Carriageway access applies to the paved strip only.',
  footAccess:
    'Whether pedestrians may use the carriageway surface itself — separate from a dedicated sidewalk path.',
  sideFacilityCondition:
    'Surface quality of this kerbside band. Stored on the child row when the contribution is approved.',
  sideFacilityMaxspeed:
    'Speed limit (km/h) for this band. Stored on the child road row; leave blank to inherit the main road limit.',
  gutter:
    'Drainage gutter beside the kerb — open gutters are common in Ghana and affect pedestrian safety.',
} as const;
